
import { useState, useEffect, useCallback, useRef } from 'react';
import { invokeEdgeFunction } from '@/lib/edge-functions';
import { getFormalName } from '@/lib/teams';

interface UserSelection {
  id: string;
  team_name: string;
  matchday: number;
  season: string;
  used_double_down: boolean;
  points_earned: number;
  match_result: 'win' | 'draw' | 'loss' | null;
  processed: boolean;
  created_at: string;
  autopicked_at: string | null;
  is_forfeit: boolean | null;
}

interface UserSelectionData {
  selections: UserSelection[];
  teamUsage: { [key: string]: number };
  tddUsage: { [key: string]: number };
  totalTddUsed: number;
  tddRemaining: number;
}

export const useUserSelections = (userId?: string, leagueId?: string, season?: string) => {
  const [selectionData, setSelectionData] = useState<UserSelectionData>({
    selections: [],
    teamUsage: {},
    tddUsage: {},
    totalTddUsed: 0,
    tddRemaining: 4
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Track the latest fetch to prevent stale responses from overwriting fresh data
  const fetchIdRef = useRef(0);

  const fetchUserSelections = useCallback(async (id?: string) => {
    const targetId = id ?? userId;
    if (!targetId) {
      setError('User not selected');
      return;
    }

    const currentFetchId = ++fetchIdRef.current;

    try {
      setIsLoading(true);

      const { data, error } = await invokeEdgeFunction('get-user-selections', {
        body: { userId: targetId, leagueId, ...(season ? { season } : {}) }
      });

      // Ignore stale responses — a newer fetch has already started
      if (currentFetchId !== fetchIdRef.current) return;

      if (error) {
        console.error('Error fetching user selections:', error);
        setError('Failed to fetch selection history');
        return;
      }

      if (data) {
        setSelectionData(data);
        setError(null);
      }
    } catch (err) {
      if (currentFetchId !== fetchIdRef.current) return;
      console.error('Error fetching user selections:', err);
      setError('Failed to fetch selection history');
    } finally {
      if (currentFetchId === fetchIdRef.current) {
        setIsLoading(false);
      }
    }
  }, [userId, leagueId, season]);

  useEffect(() => {
    if (userId && leagueId) {
      fetchUserSelections(userId);
    } else if (!userId) {
      setIsLoading(false);
    }
  }, [userId, leagueId, season, fetchUserSelections]);

  // Backend returns teamUsage/tddUsage keyed by normalized lowercase names,
  // so we normalize lookups the same way.
  const normalizeKey = useCallback((teamName: string) => {
    return getFormalName(teamName).toLowerCase();
  }, []);

  const getTeamUsageCount = useCallback((teamName: string) => {
    return selectionData.teamUsage[normalizeKey(teamName)] || 0;
  }, [selectionData.teamUsage, normalizeKey]);

  const canSelectTeam = useCallback((teamName: string) => {
    return (selectionData.teamUsage[normalizeKey(teamName)] || 0) < 2;
  }, [selectionData.teamUsage, normalizeKey]);

  const canUseTddForTeam = useCallback((teamName: string) => {
    return selectionData.tddRemaining > 0;
  }, [selectionData.tddRemaining]);

  return {
    selectionData,
    isLoading,
    error,
    getTeamUsageCount,
    canSelectTeam,
    canUseTddForTeam,
    refetch: () => fetchUserSelections()
  };
};
